import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dispatch } from 'redux';
import { DisciplinaActions } from 'Store/Disciplina/Disciplina.actions';
import { ApisEnumLabel } from 'Data/enums/Apis';
import { FileStatusInStorageEnum } from 'Data/interfaces/Obra/IDisciplineSAStatusResponse';
import {
  ActionSolutionType,
  ICreateSADisciplineRequest,
  IDeleteSADisciplineRequest,
  IMergeSADisciplineRequest,
  IRestoreSADisciplineRequest
} from 'Data/interfaces/Obra/ISolutionSADisciplineRequest';
import { Icon, IconName } from 'Components/UI';
import Button from 'Components/UI/Button';
import Modal from 'Components/UI/Modal';
import LoadingDisciplineSAConnection from 'Components/UI/CustomLoading/LoadingDisciplineSAConnection';
import ConfirmDeleteModal from '../ConfirmDeleteModal';
import { ButtonsWrapper, Card, Footer, Header, Main, Wrapper } from './styles';

interface ISolutionSAConnection {
  platform: number;
  visible?: boolean;
  onCancel: () => void;
  onConfirm: (sent: boolean) => void;
  isLoading?: boolean;
  isLoadingStatus?: boolean;
  discipline: any;
  disciplineStatus?: FileStatusInStorageEnum;
  existingFolderApiId?: string;
  dispatch: Dispatch<any>;
}

export interface IActionOption {
  id: ActionSolutionType;
  action: (discipline: any) => void;
  icon: IconName;
  iconSize: number;
  header: string;
  description: string;
}

const SolutionSAConnection: FC<ISolutionSAConnection> = ({
  platform,
  visible,
  onCancel,
  onConfirm,
  isLoading,
  isLoadingStatus,
  discipline,
  disciplineStatus,
  existingFolderApiId,
  dispatch,
}) => {
  const { t } = useTranslation('disciplinas');
  const [showDelete, setShowDelete] = useState(false);
  const [selectedOpt, setSelectedOpt] = useState<ActionSolutionType | null>(null);
  const trashed = FileStatusInStorageEnum.Trashed === disciplineStatus;
  const merged = FileStatusInStorageEnum.Recreated === disciplineStatus;
  const solutionDelete = selectedOpt === 'del';

  const actionOptions: Record<string, IActionOption> = useMemo(() => {
    const options: Record<string, IActionOption> = {
      'rest': {
        id: 'rest',
        action: (useDicipline: any) => {
          const request: IRestoreSADisciplineRequest = {
            ConstructionSiteDisciplinesId: useDicipline.ConstructionSiteDisciplinesId,
          };
          dispatch(DisciplinaActions.listArchiveNewDisciplineNoCache(true))
          dispatch(DisciplinaActions.restoreSADisciplineRequest(request))
        },
        icon: 'relogio',
        iconSize: 26,
        header: t('solutionSAConnection.restoreHeader'),
        description: t('solutionSAConnection.restoreDescription'),
      },
      'new': {
        id: 'new',
        action: (useDicipline: any) => {
          const request: ICreateSADisciplineRequest = {
            ConstructionSiteDisciplinesId: useDicipline.ConstructionSiteDisciplinesId,
          };
          if (existingFolderApiId) {
            request.existingFolderApiId = existingFolderApiId;
          }
          dispatch(DisciplinaActions.listArchiveNewDisciplineNoCache(true))
          dispatch(DisciplinaActions.createSADisciplineRequest(request))
        },
        icon: 'adicionarPasta',
        iconSize: 26,
        header: t('solutionSAConnection.createHeader'),
        description: t('solutionSAConnection.createDescription'),
      },
      'merge': {
        id: 'merge',
        action: (useDicipline: any) => {
          const request: IMergeSADisciplineRequest = {
            ConstructionSiteDisciplinesId: useDicipline.ConstructionSiteDisciplinesId,
          };
          if (existingFolderApiId) {
            request.existingFolderApiId = existingFolderApiId;
          }
          dispatch(DisciplinaActions.listArchiveNewDisciplineNoCache(true))
          dispatch(DisciplinaActions.mergeSADisciplineRequest(request))
        },
        icon: 'adicionarPasta',
        iconSize: 26,
        header: t('solutionSAConnection.mergeHeader'),
        description: t('solutionSAConnection.mergeDescription'),
      },
      'del': {
        id: 'del',
        action: (useDicipline: any) => {
          const request: IDeleteSADisciplineRequest = {
            ConstructionSiteDisciplinesId: useDicipline.ConstructionSiteDisciplinesId,
          };
          if (existingFolderApiId) {
            request.existingFolderApiId = existingFolderApiId;
          }
          dispatch(DisciplinaActions.deleteSADisciplineRequest(request))
        },
        icon: 'excluir',
        iconSize: 20,
        header: t('solutionSAConnection.deleteHeader'),
        description: t('solutionSAConnection.deleteDescription'),
      },
    };

    if (!trashed) {
      delete options['rest'];
    }

    if (!merged) {
      delete options['merge'];
    } else {
      delete options['new'];
    }

    return options;
  }, [trashed, merged, existingFolderApiId, dispatch, t]);

  const handleSelectedOpt = (opt: ActionSolutionType) => {
    setSelectedOpt(opt);
  };

  const handleConfirm = () => {
    if (solutionDelete && !showDelete) {
      return setShowDelete(true);
    }

    if (selectedOpt && discipline) {
      actionOptions[selectedOpt].action(discipline);
      return onConfirm(true);
    }
  };

  const api = ApisEnumLabel[platform];
  const disciplineName = discipline?.CustomName || discipline?.Discipline?.Name;

  const footerButtons = (
    <ButtonsWrapper disabled={`${(isLoading || !selectedOpt)}`}>
      <Button
        className="cancel"
        onClick={onCancel}
      >
        <span className="cancelText">{t('solutionSAConnection.cancel')}</span>
      </Button>
      <Button
        type="primary"
        className="confirm"
        onClick={() => handleConfirm()}
        loading={isLoading && !solutionDelete}
        disabled={isLoading || !selectedOpt}
      >
        <span className="confirmText">{t('solutionSAConnection.confirm')}</span>
      </Button>
    </ButtonsWrapper>
  );

  useEffect(() => {
    if (disciplineStatus === FileStatusInStorageEnum.Ok) {
      onCancel();
    }
  }, [disciplineStatus])

  return (
    <Modal
      width={500}
      title={t('solutionSAConnection.modalTitle')}
      visible={visible}
      onCancel={onCancel}
      footerButtons={footerButtons}
    >
      {isLoadingStatus && (
        <LoadingDisciplineSAConnection multiple={3} />
      )}
      {!isLoadingStatus && (
        <Wrapper>
          <Header>
            {trashed && (
              <span>
                {t('solutionSAConnection.headerTrashed', { name: disciplineName, api })}
              </span>
            )}
            {!trashed && (
              merged ? (
                <span>
                  {t('solutionSAConnection.headerMerged', { name: disciplineName, api })}
                </span>
              ) : (
                <span>
                  {t('solutionSAConnection.headerDeleted', { name: disciplineName, api })}
                </span>
              )
            )}
          </Header>

          <Main>
            {Object.values(actionOptions).map(option => {
              const selected = selectedOpt === option.id;

              return (
                <Card
                  key={option.id}
                  selected={`${selected}`}
                  onClick={() => handleSelectedOpt(option.id)}
                >
                  <div className="cardLeft">
                    <Icon
                      color="primary"
                      icon={option.icon}
                      customSize={option.iconSize}
                      blocked={!selected}
                    />
                  </div>
                  <div className="cardRight">
                    <span className="cardHeader">{option.header}</span>
                    <span className="cardDescription">{option.description}</span>
                  </div>
                </Card>
              )
            })}
          </Main>

          <Footer>
            <Icon
              icon="alerta"
              customSize={18}
              customColor={'#E2574C'}
            />
            <span className="footerText">{t('solutionSAConnection.footerWarning')}</span>
          </Footer>
        </Wrapper>
      )}

      {showDelete && (
        <ConfirmDeleteModal
          visible={showDelete}
          onConfirm={() => handleConfirm()}
          onCancel={() => setShowDelete(false)}
          loading={isLoading}
        />
      )}
    </Modal>
  );
}

export default SolutionSAConnection;
